import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { initiate_video_call_to_pharmacist, save_delivery_call_image } from "../../../networking/urlEndPoints";
import Axios from "../../../networking/interceptor";

/**
 * Initiates video call to pharmacist.
 * POST with form-data: pharmacy_id, patient_id.
 * Returns data: { vonage_session_id, vonage_token, application_id, patient, pharmacist }.
 * application_id is the Vonage/OpenTok API key used to join the session.
 */
export const initiateVideoCallToPharmacist = createAsyncThunk(
    'videoCallInitiate/initiate',
    async ({ pharmacy_id, patient_id }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('pharmacy_id', pharmacy_id);
            formData.append('patient_id', patient_id);
            const { data } = await Axios.post(initiate_video_call_to_pharmacist, formData);
            return data?.data ?? data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

/**
 * Saves a delivery call screenshot.
 * POST form-data: pharmacy_id, patient_id, image (file).
 */
export const saveDeliveryCallImage = createAsyncThunk(
    'videoCallInitiate/saveDeliveryCallImage',
    async ({ pharmacy_id, patient_id, imageBlob }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('pharmacy_id', pharmacy_id);
            formData.append('patient_id', patient_id);
            const fileName = `call-screenshot-${Date.now()}.png`;
            formData.append('image', imageBlob, fileName);

            const { data } = await Axios.post(save_delivery_call_image, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Accept: "application/json",
                }
            });
            return data?.data ?? data;
        } catch (error) {
            return rejectWithValue(error.response);
        }
    }
);

// Initial State – keyed by thunk/API name so it's clear which API stored the response
const initialState = {
    initiateVideoCallToPharmacist: { loading: false, data: null, error: null },
    saveDeliveryCallImage: { loading: false, data: null, error: null },
};

// Ensure state key exists (handles old persisted state or missing key)
const ensureKey = (state, key) => {
    if (!state[key]) {
        state[key] = { loading: false, data: null, error: null };
    }
};

// Helper to add reducers for a thunk (same pattern as pendingCounselling)
const handleAsyncThunk = (builder, thunk, key) => {
    builder
        .addCase(thunk.pending, (state) => {
            ensureKey(state, key);
            state[key].loading = true;
            state[key].error = null;
            state[key].data = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
            ensureKey(state, key);
            state[key].loading = false;
            state[key].data = action.payload;
            state[key].error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
            ensureKey(state, key);
            state[key].loading = false;
            state[key].data = null;
            state[key].error = action.payload;
        });
};

const videoCallInitiateSlice = createSlice({
    name: 'videoCallInitiate',
    initialState,
    reducers: {
        clearVideoCallInitiate: (state) => {
            ensureKey(state, 'initiateVideoCallToPharmacist');
            state.initiateVideoCallToPharmacist.data = null;
            state.initiateVideoCallToPharmacist.error = null;
        },
    },
    extraReducers: (builder) => {
        handleAsyncThunk(builder, initiateVideoCallToPharmacist, 'initiateVideoCallToPharmacist');
        handleAsyncThunk(builder, saveDeliveryCallImage, 'saveDeliveryCallImage');
    },
});

export const { clearVideoCallInitiate } = videoCallInitiateSlice.actions;

/** Selector: get initiate-video-call API response (vonage_session_id, vonage_token, application_id, patient, pharmacist) */
export const selectInitiateVideoCallToPharmacistData = (state) =>
    state.videoCallInitiate?.initiateVideoCallToPharmacist?.data ?? null;

export default videoCallInitiateSlice.reducer;
